import {SetStateAction, useCallback, useEffect, useState} from 'react';
import {useFormik} from 'formik';
import {MenuItem, Typography} from '@mui/material';
import {Color} from '$constants/style';
import {Button} from '@mui/material';
import {ArrowNarrowRightIcon} from '$assets/svgs';
import FrontdeskButton from '$components/FrontdeskButton';
import PatientDetailsTwoComponent from './component';
import {PatientDetailTwoFormData, patientDetailTwoFormSchema} from './schema';
import {
  useCities,
  useCountries,
  useValidatePostcode,
} from '$modules/booking/hooks';
import {OfflineBookingStep} from '../../constant';
import {FormState} from '../../types';
import './styles.scss';
import {DEFAULT_COUNTRY_CODE} from '$constants/appConstants';

interface PatientDetailsTwoContainerProps {
  offlineBookingFormData: FormState;
  setActiveStep: React.Dispatch<SetStateAction<OfflineBookingStep>>;
  setOfflineBookingFormData: React.Dispatch<React.SetStateAction<FormState>>;
}

const renderItem = (item: string) => (
  <MenuItem key={item} value={item}>
    {item}
  </MenuItem>
);

const renderValue = (value: string, placeholder: string) => (
  <Typography color={value ? undefined : Color.TextPrimayPlaceholder}>
    {value || placeholder}
  </Typography>
);

const PatientDetailsTwoContainer: React.FC<PatientDetailsTwoContainerProps> = ({
  offlineBookingFormData,
  setActiveStep,
  setOfflineBookingFormData,
}) => {
  const [isPostcodeValid, setIsPostcodeValid] = useState(false);

  const handleSubmit = () => {
    const {address, address2, postcode, city, country} = formik.values.patient;
    setOfflineBookingFormData((prevState: FormState) => ({
      appointment: {
        ...prevState.appointment,
        patient: {
          ...prevState.appointment.patient,
          address: {
            ...prevState.appointment.patient.address,
            line1: address.trim(),
            line2: address2 ? address2.trim() : '',
            city: city.trim(),
            postcode: postcode.trim(),
            country: country.trim(),
          },
        },
      },
    }));
    setActiveStep(OfflineBookingStep.ContactDetails);
  };

  const {
    isLoading: isValidatingPostcode,
    isError: isPostcodeError,
    validatePostcode,
  } = useValidatePostcode(() => {
    setIsPostcodeValid(true);
    handleSubmit();
  });

  const patientDetails = offlineBookingFormData?.appointment?.patient;

  const handleFormSubmit = () => {
    if (isPostcodeValid) {
      handleSubmit();
    } else {
      validatePostcode(
        formik.values.patient.postcode,
        formik.values.patient.country
      );
    }
  };

  const formik = useFormik<PatientDetailTwoFormData>({
    initialValues: {
      patient: {
        address: patientDetails?.address.line1 || '',
        address2: patientDetails?.address.line2 || undefined,
        postcode: patientDetails?.address.postcode || '',
        city: patientDetails?.address.city || '',
        country: patientDetails?.address.country || 'GB',
      },
    },
    validationSchema: patientDetailTwoFormSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: handleFormSubmit,
  });

  const {countries} = useCountries();
  const {cities} = useCities(formik.values?.patient?.country);
  const {setFieldTouched, setFieldValue} = formik;

  useEffect(() => {
    const initialCountry = formik.values.patient.country;
    if (initialCountry && countries.length > 0) {
      const selectedCountry = countries.find(c => c.code === initialCountry);
      if (selectedCountry) {
        setOfflineBookingFormData((prevState: FormState) => ({
          appointment: {
            ...prevState.appointment,
            country_code: selectedCountry.countryCode,
            patient: {
              ...prevState.appointment.patient,
              country_code: selectedCountry.countryCode,
            },
          },
        }));
      }
    }
  }, [countries, formik.values.patient.country, setOfflineBookingFormData]);

  const renderCountryValue = (value: string) => {
    const item = countries.find(it => it.code === value);
    if (item) value = item.name;

    return (
      <Typography
        color={value === '' ? Color.TextPrimayPlaceholder : undefined}
      >
        {value ? value : 'Select country'}
      </Typography>
    );
  };

  const renderCountryItem = (item: {code: string; name: string}) => (
    <MenuItem key={item.code} value={item.code}>
      {item.name}
    </MenuItem>
  );

  const renderCityValue = (value: string) => {
    const item = cities.find(it => it.code === value);
    if (item) value = item.name;

    return (
      <Typography
        color={value === '' ? Color.TextPrimayPlaceholder : undefined}
      >
        {value === '' ? 'Select City' : value}
      </Typography>
    );
  };

  const renderCityItem = (item: {code: string; name: string}) => (
    <MenuItem key={item.code} value={item.code}>
      {item.name}
    </MenuItem>
  );

  const handleBackButtonClick = () => {
    setActiveStep(OfflineBookingStep.PatientDetailsOne);
  };

  const handleContinueClick = () => {
    formik.submitForm();
  };

  const handleFieldChange = useCallback(
    (field: string, defaultValue: unknown) => {
      const key = `patient.${field}`;
      return (value: unknown) => {
        if (field === 'city') {
          setFieldValue(key, value || defaultValue);

          if (['title', 'gender', 'country', 'city', 'dob'].includes(field)) {
            if (value) {
              setFieldTouched(key, false);
            } else {
              setFieldTouched(key, true);
            }
          } else {
            setFieldTouched(key);
          }

          setFieldValue('patient.postcode', '');
          setFieldTouched('patient.postcode', false);
          return;
        }

        setFieldTouched(key, true, true);
        setFieldValue(key, (value as string) || (defaultValue as string), true);

        if (field === 'country') {
          setFieldValue('patient.city', '', true);
          setFieldTouched('patient.city', false);

          const selectedCountry = countries.find(c => c.code === value);

          if (selectedCountry) {
            setOfflineBookingFormData((prevState: FormState) => ({
              appointment: {
                ...prevState.appointment,
                country_code: selectedCountry.countryCode,
                patient: {
                  ...prevState.appointment.patient,
                  country_code: selectedCountry.countryCode,
                },
              },
            }));
          } else {
            setOfflineBookingFormData((prevState: FormState) => ({
              appointment: {
                ...prevState.appointment,
                country_code: DEFAULT_COUNTRY_CODE,
                patient: {
                  ...prevState.appointment.patient,
                  country_code: DEFAULT_COUNTRY_CODE,
                },
              },
            }));
          }
        }
        if (field === 'postcode' || field === 'country') {
          setIsPostcodeValid(false);
        }
      };
    },
    [setFieldTouched, setFieldValue, countries, setOfflineBookingFormData]
  );
  return (
    <div className="patient-details-two-container">
      <PatientDetailsTwoComponent
        values={formik.values}
        errors={formik.errors}
        touched={formik.touched}
        handleBlur={formik.handleBlur}
        handleChange={formik.handleChange}
        handleFieldChange={handleFieldChange}
        cities={cities}
        countries={countries}
        renderCityItem={renderCityItem}
        renderCityValue={renderCityValue}
        renderCountryItem={renderCountryItem}
        renderCountryValue={renderCountryValue}
        renderItem={renderItem}
        renderValue={renderValue}
        isPostcodeError={isPostcodeError}
      />
      <div className="patient-details-two-dialog-footer">
        <Button className="back-btn" onClick={handleBackButtonClick}>
          Back
        </Button>
        <FrontdeskButton
          title="Continue"
          className="patient-details-two-continue-btn"
          endIcon={<ArrowNarrowRightIcon />}
          onClick={handleContinueClick}
          disabled={isValidatingPostcode}
          loading={isValidatingPostcode}
        />
      </div>
    </div>
  );
};

export default PatientDetailsTwoContainer;
